import { Tooltip, styled, useTheme } from '@ori-ui/mui';
import type { GlobalCustomersDocumentApprovalDtoApprovalState } from '@ori/global-api-client-react';
import useTestId from '@ori/testid-generator';
import type { FC, ReactElement } from 'react';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { TranslationKey } from '../../models';

const RootContainer = styled('div')(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 'bold',
  lineHeight: '17px',
  textTransform: 'uppercase',
}));

const StyledSpanLabel = styled('span')(({ theme }) => ({
  padding: '2px 15px',
  borderRadius: '5px',
  color: theme.palette.primary.contrastText,
}));

interface IDocumentStateProps {
  state: GlobalCustomersDocumentApprovalDtoApprovalState;
  reason?: string;
}

export const DocumentState: FC<IDocumentStateProps> = (props) => {
  const { state, reason }: IDocumentStateProps = props;

  const theme = useTheme();

  const { getTestIdProps } = useTestId();

  const getLocalization = (approvalState: GlobalCustomersDocumentApprovalDtoApprovalState): ReactElement => {
    switch (approvalState) {
      case 'Approved':
        return (
          <Localization
            id={TranslationKey.stateApproved}
            defaultMessage={defaultTranslations.stateApproved}
          />
        );
      case 'Pending':
        return (
          <Localization
            id={TranslationKey.statePending}
            defaultMessage={defaultTranslations.statePending}
          />
        );
      case 'Rejected':
        return (
          <Localization
            id={TranslationKey.stateRejected}
            defaultMessage={defaultTranslations.stateRejected}
          />
        );
      default:
        return (
          <Localization
            id={TranslationKey.stateUnknown}
            defaultMessage={defaultTranslations.stateUnknown}
          />
        );
    }
  };

  const getStateColor = (approvalState: GlobalCustomersDocumentApprovalDtoApprovalState): string => {
    switch (approvalState) {
      case 'Approved':
        return theme.palette.secondary.main;
      case 'Pending':
        return theme.palette.warning.main;
      default:
        return 'transparent';
    }
  };

  const stateElement = (
    <RootContainer>
      <StyledSpanLabel style={{ background: getStateColor(state) }}>{getLocalization(state)}</StyledSpanLabel>
    </RootContainer>
  );

  if (reason) {
    return (
      <Tooltip
        {...getTestIdProps('reason-tooltip')}
        title={reason}
      >
        {stateElement}
      </Tooltip>
    );
  }

  return stateElement;
};
